/**
 * ATTENTION. This code was AUTO GENERATED by schema2typebox. It is based on the InputFeatureSchema
 * type in decision_engine/decision_engine/config.py. To update this file, run make gen-schema in
 * the decision_engine directory, and then copy the output here.
 */

import { type Static, Type } from "@sinclair/typebox";

export type InputFeatureSchema = Static<typeof InputFeatureSchema>;
export const InputFeatureSchema = Type.Object(
  {
    name: Type.String(),
    type: Type.Union(
      [
        Type.Literal("integer"),
        Type.Literal("float"),
        Type.Literal("categorical"),
        Type.Literal("boolean"),
        Type.Literal("passthrough"),
        Type.Literal("sparse_count"),
        Type.Literal("datetime"),
      ],
      { description: "Types of columns in the decision engine" },
    ),
    computed: Type.Optional(Type.Boolean({ default: false })),
    dependency: Type.Optional(Type.Boolean({ default: false })),
    count_categories: Type.Optional(Type.Array(Type.String(), { default: [] })),
    cross_action_features: Type.Optional(Type.Boolean({ default: false })),
    discretize: Type.Optional(
      Type.Union(
        [
          Type.Object(
            {
              num_bins: Type.Number(),
              strategy: Type.Optional(
                Type.Union(
                  [
                    Type.Literal("uniform"),
                    Type.Literal("quantile"),
                    Type.Literal("kmeans"),
                    Type.Literal("custom-quantiles"),
                  ],
                  { default: "quantile" },
                ),
              ),
              quantiles: Type.Optional(
                Type.Union([Type.Array(Type.Number()), Type.Null()], {
                  default: null,
                }),
              ),
            },
            {
              additionalProperties: false,
              description:
                "Configuration for discretizing a continuous feature.\n\nDiscretization is implemented using KBinsDiscretizer from scikit-learn or our own\nQuantileDiscretizer\n\nSee https://scikit-learn.org/stable/modules/generated/sklearn.preprocessing.KBinsDiscretizer.html#sklearn.preprocessing.KBinsDiscretizer\nfor strategy descriptions.",
            },
          ),
          Type.Null(),
        ],
        { default: null },
      ),
    ),
    imputation: Type.Optional(
      Type.Union(
        [
          Type.Object(
            {
              strategy: Type.Optional(
                Type.Union(
                  [
                    Type.Literal("mean"),
                    Type.Literal("median"),
                    Type.Literal("most_frequent"),
                    Type.Literal("constant"),
                  ],
                  { default: "mean" },
                ),
              ),
              fill_value: Type.Optional(
                Type.Union(
                  [
                    Type.String(),
                    Type.Number(),
                    Type.Number(),
                    Type.Boolean(),
                    Type.Null(),
                  ],
                  { default: null },
                ),
              ),
              add_indicator: Type.Optional(Type.Boolean({ default: true })),
            },
            {
              additionalProperties: false,
              description:
                "Configuration for imputing a feature that may have missing values.\n\nImputation is implemented using the sklearn SimpleImputer (except for bools which are custom).\nNote that defaults are set based on the type of the feature so the defaults and associated validation\nhappen in the ItemFeatureConfig class.\n\nSee https://scikit-learn.org/stable/modules/generated/sklearn.impute.SimpleImputer.html\nfor strategy descriptions.",
            },
          ),
          Type.Null(),
        ],
        { default: null },
      ),
    ),
    binarizer_threshold: Type.Optional(
      Type.Union([Type.Number(), Type.Null()], { default: null }),
    ),
    datetime_diff_config: Type.Optional(
      Type.Union(
        [
          Type.Object(
            {
              output_unit: Type.Optional(
                Type.Union([Type.Literal("hours"), Type.Literal("days")], {
                  default: "hours",
                }),
              ),
            },
            {
              additionalProperties: false,
              description:
                'Configuration for computing time differences from datetime dependency features.\n\nThis computes the difference between a target datetime column (e.g., send_at) and a\ndatetime dependency feature (e.g., unpause_date). Both datetimes are expected to be\nin UTC timezone.\n\nThe calculation is: target_datetime - reference_datetime_feature\n- Positive values indicate the reference is in the past\n- Negative values indicate the reference is in the future\n\nExample:\n    If unpause_date is "2025-01-15 10:00:00 UTC" and send_at is "2025-01-15 12:00:00 UTC",\n    with output_unit="hours", the result would be 2.0 hours (unpause_date is 2 hours ago).',
            },
          ),
          Type.Null(),
        ],
        { default: null },
      ),
    ),
  },
  {
    $defs: {
      DatetimeDiffConfig: {
        additionalProperties: false,
        description:
          'Configuration for computing time differences from datetime dependency features.\n\nThis computes the difference between a target datetime column (e.g., send_at) and a\ndatetime dependency feature (e.g., unpause_date). Both datetimes are expected to be\nin UTC timezone.\n\nThe calculation is: target_datetime - reference_datetime_feature\n- Positive values indicate the reference is in the past\n- Negative values indicate the reference is in the future\n\nExample:\n    If unpause_date is "2025-01-15 10:00:00 UTC" and send_at is "2025-01-15 12:00:00 UTC",\n    with output_unit="hours", the result would be 2.0 hours (unpause_date is 2 hours ago).',
        properties: {
          output_unit: {
            default: "hours",
            enum: ["hours", "days"],
            title: "DatetimeDiffConfigUnit",
            type: "string",
          },
        },
        title: "DatetimeDiffConfig",
        type: "object",
      },
      DatetimeDiffConfigUnit: {
        enum: ["hours", "days"],
        title: "DatetimeDiffConfigUnit",
        type: "string",
      },
      DecisionEngineColumnType: {
        description: "Types of columns in the decision engine",
        enum: [
          "integer",
          "float",
          "categorical",
          "boolean",
          "passthrough",
          "sparse_count",
          "datetime",
        ],
        title: "DecisionEngineColumnType",
        type: "string",
      },
      DiscretizationConfig: {
        additionalProperties: false,
        description:
          "Configuration for discretizing a continuous feature.\n\nDiscretization is implemented using KBinsDiscretizer from scikit-learn or our own\nQuantileDiscretizer\n\nSee https://scikit-learn.org/stable/modules/generated/sklearn.preprocessing.KBinsDiscretizer.html#sklearn.preprocessing.KBinsDiscretizer\nfor strategy descriptions.",
        properties: {
          num_bins: { title: "Num Bins", type: "integer" },
          strategy: {
            default: "quantile",
            enum: ["uniform", "quantile", "kmeans", "custom-quantiles"],
            title: "Strategy",
            type: "string",
          },
          quantiles: {
            anyOf: [
              { items: { type: "number" }, type: "array" },
              { type: "null" },
            ],
            default: null,
            title: "Quantiles",
          },
        },
        required: ["num_bins"],
        title: "DiscretizationConfig",
        type: "object",
      },
      ImputationConfig: {
        additionalProperties: false,
        description:
          "Configuration for imputing a feature that may have missing values.\n\nImputation is implemented using the sklearn SimpleImputer (except for bools which are custom).\nNote that defaults are set based on the type of the feature so the defaults and associated validation\nhappen in the ItemFeatureConfig class.\n\nSee https://scikit-learn.org/stable/modules/generated/sklearn.impute.SimpleImputer.html\nfor strategy descriptions.",
        properties: {
          strategy: {
            default: "mean",
            enum: ["mean", "median", "most_frequent", "constant"],
            title: "Strategy",
            type: "string",
          },
          fill_value: {
            anyOf: [
              { type: "string" },
              { type: "number" },
              { type: "integer" },
              { type: "boolean" },
              { type: "null" },
            ],
            default: null,
            title: "Fill Value",
          },
          add_indicator: {
            default: true,
            title: "Add Indicator",
            type: "boolean",
          },
        },
        title: "ImputationConfig",
        type: "object",
      },
    },
    additionalProperties: false,
    description:
      "Class to represent the schema of a user or item feature. This is used to define transformations,\ngenerate the schema for the interactions dataframe, etc.",
    $id: "InputFeatureSchema",
  },
);
